import {
  GLOBAL_SEARCH_GROUP_LIMIT,
  GLOBAL_SEARCH_MIN_QUERY_LENGTH,
  GLOBAL_SEARCH_STATIC_ENTRIES,
} from "@frontend-shared/constants";
import {
  EGlobalSearchGate,
  EGlobalSearchItemType,
  type IGlobalSearchGroup,
  type IGlobalSearchStaticEntry,
} from "@frontend-shared/types";
import {
  buildGlobalSearchActionItems,
  buildGlobalSearchFaqItems,
  buildGlobalSearchIntegrationProviderItems,
  buildGlobalSearchOrganizationItems,
  buildGlobalSearchProjectItems,
  searchGlobalSearchStaticEntries,
} from "@frontend-shared/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/hooks/use-auth";
import { useFaqData } from "@/pages/faq/hooks/use-faq-data";

import type { IUseGlobalSearchClientGroupsProps } from "../types";
import { useGlobalSearchGateResolver } from "./use-global-search-gate-resolver";
import { useGlobalSearchIntegrationProviders } from "./use-global-search-integration-providers";
import { useGlobalSearchTranslate } from "./use-global-search-translate";

const STATIC_TYPES = [
  EGlobalSearchItemType.PAGE,
  EGlobalSearchItemType.SETTINGS_TAB,
  EGlobalSearchItemType.SETTINGS_OPTION,
] as const;

// NOTE: One index per static type, so each group gets its own cap instead of
// pages crowding the settings options out of a shared one.
const STATIC_ENTRIES_BY_TYPE: ReadonlyArray<
  readonly [EGlobalSearchItemType, readonly IGlobalSearchStaticEntry[]]
> = STATIC_TYPES.map((type) => [
  type,
  GLOBAL_SEARCH_STATIC_ENTRIES.filter((entry) => entry.type === type),
]);

// NOTE: NSAI-6589 — every group built on the device: the static index (pages,
// settings tabs, settings options) filtered through web's gate resolver, the
// projects and organizations the user belongs to, the FAQ, the connector
// catalog and the fallback actions. None of it depends on /search/global, so a
// failing server call never takes these results down with it.
export const useGlobalSearchClientGroups = ({
  query,
  isEnabled,
}: IUseGlobalSearchClientGroupsProps): IGlobalSearchGroup[] => {
  const { t } = useTranslation("globalSearch");
  const { projects, currentProjectUuid } = useAuth();
  const translate = useGlobalSearchTranslate();
  const isGateOpen = useGlobalSearchGateResolver({ isEnabled });
  const isSearchable = query.trim().length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH;
  const { data: faqCategories } = useFaqData({
    isEnabled: isEnabled && isSearchable,
  });
  const hasIntegrationRead = isGateOpen(EGlobalSearchGate.INTEGRATION_READ);
  const integrationProviders = useGlobalSearchIntegrationProviders({
    isEnabled: isEnabled && hasIntegrationRead,
    translate,
  });

  return useMemo(() => {
    if (!isSearchable) return [];

    const staticGroups = STATIC_ENTRIES_BY_TYPE.map(([type, entries]) => ({
      type,
      items: searchGlobalSearchStaticEntries({
        query,
        entries,
        translate,
        isGateOpen,
        limit: GLOBAL_SEARCH_GROUP_LIMIT,
      }),
    }));

    return [
      ...staticGroups,
      {
        type: EGlobalSearchItemType.PROJECT,
        items: buildGlobalSearchProjectItems({
          query,
          projects,
          currentProjectUuid,
          personalLabel: t("badges.personal"),
          limit: GLOBAL_SEARCH_GROUP_LIMIT,
        }),
      },
      {
        type: EGlobalSearchItemType.ORGANIZATION,
        items: buildGlobalSearchOrganizationItems({
          query,
          projects,
          limit: GLOBAL_SEARCH_GROUP_LIMIT,
        }),
      },
      {
        type: EGlobalSearchItemType.INTEGRATION_PROVIDER,
        items: hasIntegrationRead
          ? buildGlobalSearchIntegrationProviderItems({
              query,
              providers: integrationProviders,
              limit: GLOBAL_SEARCH_GROUP_LIMIT,
            })
          : [],
      },
      {
        type: EGlobalSearchItemType.FAQ_ITEM,
        items: buildGlobalSearchFaqItems({
          query,
          categories: faqCategories ?? [],
          limit: GLOBAL_SEARCH_GROUP_LIMIT,
        }),
      },
      {
        type: EGlobalSearchItemType.ACTION,
        items: buildGlobalSearchActionItems({
          query,
          labels: {
            searchHistory: t("actions.search_history", { query: query.trim() }),
            searchDocs: t("actions.search_docs", { query: query.trim() }),
          },
          isDocsAvailable: true,
        }),
      },
    ].filter((group) => group.items.length > 0);
  }, [
    isSearchable,
    query,
    translate,
    isGateOpen,
    projects,
    currentProjectUuid,
    t,
    hasIntegrationRead,
    integrationProviders,
    faqCategories,
  ]);
};
